<template>
  <div class="file-uploader">
    <input
      type="file"
      accept=".csv, .xlsx, .xls"
      :id="`FILE_INPUT_${uploaderKey}`"
      @change="handleFileChange"
      class="file-uploader__input"
    />
    <label
      :for="`FILE_INPUT_${uploaderKey}`"
      class="file-uploader__label"
      :class="[
        { 'file-uploader__label--error': error },
      ]"
      :title="(error) ? error : 'File uploader'"
    >
      <slot></slot>
    </label>
  </div>
</template>

<script setup lang="ts">
import { ref, watchEffect } from 'vue';
import { E_FILE_UPLOADER_EVENT } from '../../types/components-events.enums';
import type { TFileUploaderEventResponse } from '../../types/event-response.types';
import ArtifactProcessingService from '../../services/artifact-processing.service';
import type { E_SOURCE_TYPE, TSourceData } from '../../types/artifact-processing.types';
import DocumentProcessingService from '../../services/document-processing.service';

interface IProps {
  validExtensions: string[];
  uploaderKey: string;
}
const props = defineProps<IProps>();
const emit = defineEmits<{
  (e: E_FILE_UPLOADER_EVENT.UPDATE, payload: TFileUploaderEventResponse[E_FILE_UPLOADER_EVENT.UPDATE]): void;
  (e: E_FILE_UPLOADER_EVENT.ERROR, payload: TFileUploaderEventResponse[E_FILE_UPLOADER_EVENT.ERROR]): void;
}>();

const fileName = ref('');
const error = ref('');

const documentProcessingService = new DocumentProcessingService();

watchEffect(() => {
  if (error.value) {
    console.error(error.value);
  }
})

function isValidFileType(file: any): boolean {
  const fileName = file.name;
  if (!fileName) return false;
  const commaIndex = fileName.lastIndexOf('.');
  const fileExtension = fileName.slice(commaIndex)?.toLowerCase();
  const isValid = props.validExtensions.includes(fileExtension);
  return isValid;
};

function processCSV(csv: string) {
  const { rawTables, surfacesColumns } = documentProcessingService.parseCSV(csv);
  const preparedTables = rawTables.map((table) => clearArtifacts<E_SOURCE_TYPE.ARRAY_OF_DICTIONARIES>(table));
  const data = preparedTables.filter((table) => !!table);
  return  { tables: data, surfacesColumns };
};

function processExcel(excel: ArrayBuffer) {
  const { rawTables, surfacesColumns } = documentProcessingService.parseExcel(excel);
  const preparedTables = rawTables.map((table) => clearArtifacts<E_SOURCE_TYPE.ARRAY_OF_DICTIONARIES>(table));
  const data = preparedTables.filter((table) => !!table);
  return { tables: data, surfacesColumns };
}

function clearArtifacts<SourceType extends E_SOURCE_TYPE>(rawData: TSourceData[SourceType]) {
  const artifactProcessingService = new ArtifactProcessingService<SourceType>(rawData);
  artifactProcessingService.process();
  const preparedTable = artifactProcessingService.result;
  if (preparedTable && preparedTable.length > 0) {
    return preparedTable;
  } else {
    return null;
  }
}

function handleFileChange(event: any) {
  if (!event.target) return;
  const file = event.target.files[0];
  
  if (!file) return;
  
  fileName.value = file.name;
  error.value = '';
  
  if (!isValidFileType(file)) {
    error.value = 'Invalid file format. CSV, XLSX and XLS are supported';
    emit(E_FILE_UPLOADER_EVENT.ERROR, error.value);
    return;
  }

  const reader = new FileReader();
  reader.onload = (e) => catchDataFromReader(e, fileName.value);
  reader.onerror = () => {
    error.value = 'File reading error';
    emit(E_FILE_UPLOADER_EVENT.ERROR, error.value);
  };
  
  // For Excel files we read as arraybuffer, for CSV as text
  if (file.name.endsWith('.csv')) {
    reader.readAsText(file);
  } else {
    reader.readAsArrayBuffer(file);
  }
};

function catchDataFromReader(e: any, fileName: string) {
  try {
    const fileFormat = {
      isCSV: fileName.endsWith('.csv'),
      isExcel: fileName.endsWith('.xlsx') || fileName.endsWith('.xls'),
    }

    if (fileFormat.isCSV) {
      const csv = e.target.result as string;
      const { tables, surfacesColumns } = processCSV(csv);
      emit(E_FILE_UPLOADER_EVENT.UPDATE, {
        tables,
        surfacesColumns,
      });
      return;
    }
    
    if (fileFormat.isExcel) {
      const excel = e.target.result as ArrayBuffer;
      const { tables, surfacesColumns } = processExcel(excel);
      emit(E_FILE_UPLOADER_EVENT.UPDATE, {
        tables,
        surfacesColumns,
      });
      return;
    }

    throw new Error('Unsupported file type');
  } catch (err: any) {
    error.value = 'File processing error: ' + err.message;
    emit(E_FILE_UPLOADER_EVENT.ERROR, error.value);
  }
}
</script>

<style scoped lang="scss">
.file-uploader {
  display: flex;
  position: relative;

  &__input {
    display: none;
    position: absolute;
    left: -500px;
  }

  &__label {
    display: flex;
    align-items: center;
    justify-content: center;

    &--error {
      color: #e74c3c!important;
      & > * {
        color: #e74c3c!important;
      }
    }
  }

  &__error-message {
    color: #e74c3c;
    margin-top: 0.5rem;
  }
}
</style>